import { ListItem, Typography, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import {
  getExpandState,
  yearChoice,
} from "../../../../common/services/Utilities";
import {
  KeyValue,
  FormSelectChoice,
  Query,
  Form,
} from "../../../../interfaces";
import {
  GrowthRateReportChoicesBHCs,
  GrowthRateReportChoicesBanks,
  defaultSearchFieldInputLookup,
  growthRateDisplayResultOptions,
  rssdRegEx,
} from "../../../../refdata/constants";
import SelectFilterField from "../../../SelectFilterField";
import AccordionDropdown from "../../../../containers/AccordionDropdown";
import MultiDropdownInput from "../../../../containers/MultiDropdownInput";
import useLastYearFilter from "../../../../hooks/useLastYearFilter";
import useGetStateList from "../../../../hooks/useGetStateList";
import BankBHCsToggle from "../../../BankBHCsToggle";

const PREFIX = "GrowthRateSearchForm";

const classes = {
  title: `${PREFIX}-title`,
};

const Root = styled("div")(() => ({
  [`& .${classes.title}`]: { paddingTop: 16, lineHeight: "1rem" },
}));

export interface GrowthRateForm extends Form {
  reportType: string;
  lastYear: string;
  endDate: string;
  startDate: string;
  displayResults: string;
  stalp: string;
  name: string;
  institutionType: string;
  displayAll: boolean;
  locations: string;
  stalpbr: string;
  stateNames: string[];
  msa: string;
  marketSelection: KeyValue[];
  viewBCHandInt: boolean;
  zipbr: string;
}

interface Props {
  appState: Query;
  searchForm: GrowthRateForm;
  formMetaData: KeyValue;
  setSearchForm: (searchForm: GrowthRateForm) => void;
  updateFilter: (filterField: string, value: string | number) => void;
  errors: Set<string>;
}

const bankFormValues = ["cert", "name"];
const bhcFormValues = ["bhcName", "bhcRSSD"];

const label: FormSelectChoice = {
  label: "All States and Territories",
  value: "ALL",
};

const growthRateSearchFieldInputs = {
  ...defaultSearchFieldInputLookup,
  name: {
    ...defaultSearchFieldInputLookup.name,
    apiPath: "sod",
    uniqueField: "NAMEFULL",
  },
  bhcName: {
    ...defaultSearchFieldInputLookup.bhcName,
    apiPath: "sod",
    uniqueField: "NAMEHCR",
  },
  bhcRSSD: {
    placeholder: "Enter Holding Company ID",
    label: "Bank Holding Company ID",
    apiPath: "sod",
    uniqueField: "RSSDHCR",
    regEx: rssdRegEx,
    autoComplete: false,
  },
};
const divider = (
  <Divider
    style={{ margin: 0, backgroundColor: "#acc8df" }}
    variant="middle"
    component="li"
  />
);

export default function GrowthRateSearchForm({
  appState,
  searchForm,
  formMetaData,
  setSearchForm,
  errors,
  updateFilter,
}: Props) {
  const stateList = useGetStateList(searchForm, setSearchForm);

  React.useEffect(() => {
    if (!searchForm.displayResults) {
      setSearchForm({
        ...searchForm,
        stalpbr: "",
        displayResults: "statesByCounty",
      });
    }
  }, [searchForm, setSearchForm]);

  const { lastYear } = useLastYearFilter(appState.filters, updateFilter, true);
  const growthTablesReportingPeriods = yearChoice(1994, lastYear, "06/30/");

  return (
    <Root>
      <ListItem>
        <AccordionDropdown
          id="institutionType"
          title="Institution Type"
          defaultExpand={getExpandState(
            appState.ui.institutionTypeExpand,
            true,
          )}
          body={
            <BankBHCsToggle
              form={searchForm}
              setForm={setSearchForm}
              bankValues={bankFormValues}
              bhcValues={bhcFormValues}
            />
          }
        />
      </ListItem>
      <ListItem>
        {searchForm.institutionType === "bhcs" && (
          <AccordionDropdown
            id="bhcType"
            title={"RSSDHCR / BHC Name"}
            defaultExpand={getExpandState(
              appState.ui.institutionTypeExpand,
              true,
            )}
            body={
              <MultiDropdownInput
                selectId="bhcName"
                form={searchForm}
                setForm={setSearchForm}
                initialSearchFieldName={
                  appState.search.bhcName
                    ? "bhcName"
                    : appState.filters.bhcRSSD
                      ? "bhcRSSD"
                      : ""
                }
                formSelectChoices={GrowthRateReportChoicesBHCs}
                searchFieldInputLookup={growthRateSearchFieldInputs}
                errors={errors}
                formMetaData={formMetaData}
              />
            }
          />
        )}
        {searchForm.institutionType === "banks" && (
          <AccordionDropdown
            id="institutionType"
            title={"Bank Name / Cert"}
            defaultExpand={getExpandState(
              appState.ui.institutionTypeExpand,
              true,
            )}
            body={
              <MultiDropdownInput
                selectId="name-cert"
                form={searchForm}
                setForm={setSearchForm}
                initialSearchFieldName={
                  appState.search.name
                    ? "name"
                    : appState.filters.cert
                      ? "cert"
                      : ""
                }
                formSelectChoices={GrowthRateReportChoicesBanks}
                searchFieldInputLookup={growthRateSearchFieldInputs}
                errors={errors}
                formMetaData={formMetaData}
              />
            }
          />
        )}
      </ListItem>
      <ListItem>
        <Typography className={classes.title} variant="h4">
          Reporting Period Range
        </Typography>
        <SelectFilterField
          helperText={
            searchForm.startDate > searchForm.endDate
              ? "End Date can not be before Start Date"
              : undefined
          }
          label={"Start Date"}
          currentValue={searchForm.startDate}
          fieldName="startDate"
          options={growthTablesReportingPeriods}
          form={searchForm}
          noEmptyVal
          setForm={setSearchForm}
          error={searchForm.startDate > searchForm.endDate}
          disabled={false}
        />
        <SelectFilterField
          helperText={
            searchForm.startDate > searchForm.endDate
              ? "End Date can not be before Start Date"
              : undefined
          }
          label={"End Date"}
          currentValue={searchForm.endDate}
          fieldName="endDate"
          options={growthTablesReportingPeriods}
          form={searchForm}
          noEmptyVal
          setForm={setSearchForm}
          error={searchForm.startDate > searchForm.endDate}
          disabled={false}
        />
      </ListItem>
      {divider}
      <ListItem>
        <AccordionDropdown
          id="displayResults"
          title="Display Results"
          defaultExpand={getExpandState(
            appState.ui.institutionTypeExpand,
            true,
          )}
          body={
            <>
              <SelectFilterField
                currentValue={
                  searchForm.displayResults
                    ? searchForm.displayResults
                    : "statesByCounty"
                }
                fieldName="displayResults"
                options={growthRateDisplayResultOptions}
                form={searchForm}
                setForm={setSearchForm}
              />
              {searchForm.displayResults === "statesByCounty" && (
                <SelectFilterField
                  currentValue={searchForm.stalpbr}
                  fieldName={"stalpbr"}
                  options={[label, ...stateList]}
                  form={searchForm}
                  setForm={setSearchForm}
                />
              )}
            </>
          }
        />
      </ListItem>
    </Root>
  );
}
